import { useCallback, useEffect, useState } from "react";
import { Plug, RefreshCw, Trash2, X, ShoppingBag } from "lucide-react";
import api, { fmtDateTime, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-shipany-blue focus:outline-none";
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Integrations() {
  const [platforms, setPlatforms] = useState([]);
  const [connections, setConnections] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [creds, setCreds] = useState({});
  const [syncing, setSyncing] = useState("");

  const load = useCallback(() => {
    api.get("/integrations/platforms").then(({ data }) => setPlatforms(data)).catch(() => {});
    api.get("/integrations").then(({ data }) => setConnections(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    try {
      const { data } = await api.post("/integrations/connect", { platform: connecting.code, ...creds });
      if (data.status === "connected") toast.success(data.note || "Connected!");
      else if (data.status === "pending_activation") toast.info(data.note);
      else toast.error(data.note || "Connection failed");
      setConnecting(null);
      setCreds({});
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const sync = async (c) => {
    setSyncing(c.connection_id);
    try {
      const { data } = await api.post(`/integrations/${c.connection_id}/sync`);
      toast.success(`Sync complete — ${data.imported} new orders imported`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSyncing(""); }
  };

  return (
    <div data-testid="integrations-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Integrations</h1>
      <p className="text-sm text-slate-500">Connect stores — orders sync in, tracking syncs back.</p>

      <h2 className="font-display mt-6 text-sm font-bold uppercase tracking-widest text-slate-400">Connected stores</h2>
      <div className="mt-3 space-y-3" data-testid="connections-list">
        {connections.map((c) => (
          <div key={c.connection_id} className="flex flex-wrap items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`connection-${c.connection_id}`}>
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-navy text-shipany-cyan"><ShoppingBag className="h-5 w-5" /></span>
            <div className="min-w-40 flex-1">
              <p className="font-semibold capitalize text-shipany-navy">{c.platform} <span className="mono ml-1 text-xs font-normal text-slate-400">{c.store_url}</span></p>
              <p className="text-xs text-slate-400">{c.note} · {c.order_count} orders · {c.last_sync_at ? `synced ${fmtDateTime(c.last_sync_at)}` : "never synced"}</p>
            </div>
            <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${c.status === "connected" ? "bg-emerald-50 text-emerald-600" : c.status === "error" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{c.status.replace(/_/g, " ").toUpperCase()}</span>
            {c.platform === "custom" && (
              <button onClick={() => { navigator.clipboard?.writeText(`${API_BASE}/api/integrations/webhook/${c.connection_id}`); toast.success("Webhook URL copied"); }} data-testid={`copy-webhook-${c.connection_id}`}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-shipany-blue hover:bg-shipany-blue/5">Copy webhook URL</button>
            )}
            <button onClick={() => sync(c)} disabled={syncing === c.connection_id} data-testid={`sync-${c.connection_id}`}
              className="flex items-center gap-1.5 rounded-lg bg-shipany-blue px-3.5 py-1.5 text-xs font-bold text-white hover:bg-[#0648B3] disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing === c.connection_id ? "animate-spin" : ""}`} /> Sync Orders
            </button>
            <button onClick={async () => { if (window.confirm("Disconnect store?")) { await api.delete(`/integrations/${c.connection_id}`); load(); } }} data-testid={`disconnect-${c.connection_id}`}
              className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!connections.length && <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-400">No stores connected yet.</div>}
      </div>

      <h2 className="font-display mt-8 text-sm font-bold uppercase tracking-widest text-slate-400">Available platforms</h2>
      <div className="mt-3 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="platforms-grid">
        {platforms.map((p) => (
          <div key={p.code} className="rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg" data-testid={`platform-${p.code}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-shipany-blue/10 text-shipany-blue"><Plug className="h-5 w-5" /></span>
              {p.status === "stub" && <span className="rounded bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-600">COMING SOON</span>}
            </div>
            <h3 className="font-display mt-3 font-bold text-shipany-navy">{p.name}</h3>
            <p className="mt-1 text-xs text-slate-400">{p.status === "active" ? "Order sync · tracking sync · fulfillment" : "Adapter ready — activates with official API access"}</p>
            <button onClick={() => { setConnecting(p); setCreds({}); }} data-testid={`connect-${p.code}`}
              className="mt-4 w-full rounded-xl border-2 border-shipany-blue/20 py-2 text-xs font-bold text-shipany-blue transition-colors hover:bg-shipany-blue hover:text-white">
              Connect
            </button>
          </div>
        ))}
      </div>

      {connecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="connect-modal">
          <div className="absolute inset-0 bg-shipany-navy/60 backdrop-blur-sm" onClick={() => setConnecting(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6">
            <div className="flex items-center justify-between"><h2 className="font-display text-lg font-bold text-shipany-navy">Connect {connecting.name}</h2><button onClick={() => setConnecting(null)} className="text-slate-400"><X className="h-5 w-5" /></button></div>
            <div className="mt-4 space-y-3.5">
              {connecting.fields.length === 0 && <p className="text-sm text-slate-500">Custom stores push orders to a SHIPANY webhook URL. No credentials needed — connect to generate yours.</p>}
              {connecting.fields.map((f) => (
                <input key={f} data-testid={`connect-field-${f}`} type={f.includes("secret") || f.includes("token") || f.includes("key") ? "password" : "text"}
                  className={inputCls} placeholder={f.replace(/_/g, " ")} value={creds[f] || ""} onChange={(e) => setCreds({ ...creds, [f]: e.target.value })} />
              ))}
              {connecting.code === "shopify" && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">Admin API access token from your Shopify custom app (Settings → Apps → Develop apps).</p>}
              {connecting.code === "woocommerce" && <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">REST API keys from WooCommerce → Settings → Advanced → REST API.</p>}
            </div>
            <button onClick={connect} data-testid="connect-confirm-btn" className="mt-5 w-full rounded-xl bg-shipany-blue py-3 text-sm font-bold text-white hover:bg-[#0648B3]">Connect Store</button>
          </div>
        </div>
      )}
    </div>
  );
}
